import { Divider, Text } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import { Fragment, useContext } from "react";
import { CombinedPaymentContext } from "../../../shared/PersistentSummary/CombinedPaymentContext";
import type { CoverItem } from "./CoverSection";
import { CoverSection } from "./CoverSection";

export type ProductSummaryProps = {
  /** Primary (non-optional) covers */
  covers: CoverItem[];
  /** Optional/additional covers */
  additionalCovers: CoverItem[];
  /** Formatted policy start date (e.g., "January 15, 2025") */
  policyStartDate?: string;
  /** Affects premium display and dividers for shared-limit products (e.g., BOP) */
  hasSharedLimits?: boolean;
};

export const ProductSummary = ({
  covers,
  additionalCovers,
  policyStartDate,
  hasSharedLimits,
}: ProductSummaryProps) => {
  const isCombinedPaymentPage = useContext(CombinedPaymentContext);

  return (
    <div
      className={classNames("mobius", "mobius-product-summary", {
        "mobius-product-summary--combined": isCombinedPaymentPage,
      })}
      data-testid="product-summary"
    >
      {covers.map((cover, idx) => {
        const isFirstCover = idx === 0;
        const hasPolicyDate = !!policyStartDate;
        const hasPremium = cover.premium !== "";

        // Shared limits (BOP): policy date only on the first cover
        // Separate covers: policy date on every cover
        const showPolicyDate = hasSharedLimits
          ? hasPolicyDate && isFirstCover
          : hasPolicyDate;

        // Shared limits (BOP): divider separates the first cover's date from its limits
        const showDividerAfterPolicyDate =
          hasSharedLimits && hasPolicyDate && isFirstCover;

        // Separate covers: divider separates each cover's date from its limits
        const showDividerAfterCoverHeader =
          !hasSharedLimits && hasPremium && hasPolicyDate;

        // Shared limits (BOP): divider separates subsequent covers from each other
        const showDividerAfterCoverDetails = hasSharedLimits && !isFirstCover;

        return (
          <Fragment key={cover.name}>
            <CoverSection
              cover={cover}
              afterHeader={
                showPolicyDate ? (
                  <>
                    <div className="mobius-product-summary__policy-date">
                      <div className="mobius-product-summary__detail-row">
                        <Text
                          elementType="span"
                          className="mobius-product-summary__detail-text"
                        >
                          {`Policy starting ${policyStartDate}`}
                        </Text>
                        <span className="mobius-product-summary__detail-text mobius-product-summary__detail-value" />
                      </div>
                    </div>
                    {(showDividerAfterPolicyDate ||
                      showDividerAfterCoverHeader) && <Divider />}
                  </>
                ) : undefined
              }
              afterDetails={
                showDividerAfterCoverDetails ? <Divider /> : undefined
              }
            />
          </Fragment>
        );
      })}
      {additionalCovers.map(cover => (
        <Fragment key={cover.name}>
          <div className="mobius-product-summary__additional-separator" />
          <CoverSection cover={cover} />
        </Fragment>
      ))}
    </div>
  );
};

ProductSummary.displayName = "ProductSummary";
